<script>
import BlockContent from '../UI/Content/BlockContent.vue'
import DataTable from '../UI/Table/DataTable.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import { isTheme } from '../../Utils/isTheme'

export default {
  components: { DataTable, BlockContent },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    entityId: { type: Number, required: true },
    filter: { type: String, required: true },
  },

  data() {
    return {
      entity: null,
      reloadList: 0,
    }
  },

  computed: {
    columns() {
      return [
        { name: 'id', displayable: false },
        {
          name: 'number',
          title: this.$t('invoice.list.number'),
          formatter: 'context',
          style: { className: 'text-center' },
          options: { renderType: 'invoice' },
        },
        {
          displayable: !isTheme(['financia']),
          name: 'companyName',
          title: this.$t('invoice.list.companyName'),
          formatter: 'entity',
          options: { renderId: 'companyId' },
        },
        {
          name: 'contractNumber',
          title: this.$t('invoice.list.contractNumber'),
          formatter: 'context',
          style: { className: 'text-center' },
          options: { renderType: 'contracts', renderId: 'contractId' },
        },
        {
          name: 'status',
          title: this.$t('invoice.list.status'),
          formatter: 'status',
          style: { className: 'text-center' },
          options: { renderType: 'invoice' },
        },
        {
          name: 'createdAt',
          title: this.$t('invoice.list.createdAt'),
          type: 'date',
          formatter: 'date',
        },
        {
          displayable: !isTheme(['financia']),
          name: 'dueAt',
          title: this.$t('invoice.list.dueAt'),
          type: 'date',
          formatter: 'date',
        },
        {
          name: 'paidAt',
          title: this.$t('entity.invoice.list.paidAt'),
          type: 'date',
          formatter: 'date',
        },
        {
          name: 'signedAmount',
          title: this.$t('invoice.list.amount'),
          type: 'num-fmt',
          formatter: 'currency',
          style: { className: 'col-align-right' },
        },
        {
          name: 'signedAmountVat',
          title: this.$t('invoice.list.amountVat'),
          type: 'num-fmt',
          formatter: 'currency',
          style: { className: 'col-align-right' },
        },
        {
          name: 'balance',
          title: this.$t('entity.invoice.list.balance'),
          type: 'num-fmt',
          formatter: 'currency',
          style: { className: 'col-align-right' },
        },
      ]
    },

    searchParams() {
      const searchParams = new URLSearchParams()
      searchParams.set('type', this.filter.toUpperCase())
      searchParams.set('entity', this.entityId)
      return searchParams
    },
  },

  methods: {
    async getEntity() {
      const data = await this.getJSON(`entities/${this.entityId}`)
      this.entity = data.entity
    },

    async getInvoices(callback, { page, limit, search, sort = [] }) {
      const searchParams = new URLSearchParams(this.searchParams.toString())
      searchParams.set('page', page)
      searchParams.set('limit', limit)
      for (const { field, order } of sort) {
        searchParams.set(`sort[${field}]`, order)
      }
      if (!!search) {
        searchParams.set('q', search)
      }
      const { items: invoices, total } = await this.getJSON(`invoices?${searchParams.toString()}`)
      callback({ data: invoices, recordsTotal: total, recordsFiltered: total })
    },

    formatTotal(...keys) {
      const total = keys.reduce(
        (sum, key) => (sum === null || this.entity === null ? null : sum + Number(this.entity[this.filter + key])),
        0,
      )
      return total === null ? '-' : this.$n(total, 'currency')
    },
  },

  beforeMount() {
    this.getEntity()
  },

  watch: {
    filter() {
      this.$refs.invoiceList.renderDataTable()
    },
  },
}
</script>
<template>
  <BlockContent :title-t="'entity.invoice.totals.title.' + filter" title-level="5" color="primary">
    <table class="table table-responsive table-striped table-hover fs-16">
      <thead>
        <tr>
          <th class="col-nb">{{ $t('entity.invoice.totals.amountExclVat') }}</th>
          <th class="col-nb">{{ $t('entity.invoice.totals.amountInclVat') }}</th>
          <th class="col-nb">{{ $t('entity.invoice.totals.balance') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="col-nb">{{ formatTotal('TotalAmount') }}</td>
          <td class="col-nb">{{ formatTotal('TotalAmount', 'TotalTax') }}</td>
          <td class="col-nb">{{ formatTotal('NetBalance') }}</td>
        </tr>
      </tbody>
    </table>
  </BlockContent>
  <BlockContent>
    <DataTable
      ref="invoiceList"
      id="invoiceList"
      :columns="columns"
      :fetch="getInvoices"
      :order="[{ dir: 'desc', name: 'createdAt' }]"
      :zero-records="$t('entity.invoice.list.zeroRecord')"
      pagination-mode="server"
    />
  </BlockContent>
</template>
